import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import PageHeader from "src/components/common/PageHeader";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as purchaseActions from "src/redux/slices/purchaseSlice";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter, formatCreatedBy } from "src/utils/common";
import { Badge } from "src/components/ui/Badge";
import usePlanUsage from "src/hooks/usePlanUsage";

const PurchaseListPage: React.FC = () => {
  const [limit] = useState(10);

  const { list } = useAppSelector((state) => state.purchase);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreatePurchase =
    isTenantAdmin || (permissions?.purchases || []).includes("create");
  const canEditPurchase =
    isTenantAdmin || (permissions?.purchases || []).includes("edit");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { planUsage } = usePlanUsage();
  const isRowOwner = (row: any) => {
    const createdById =
      typeof row?.createdBy === "string" ? row.createdBy : row?.createdBy?._id;
    if (!createdById || !user?._id) return false;
    return String(createdById) === String(user._id);
  };

  const purchaseLimit = planUsage?.plan?.features?.totalPurchaseOrders;
  const transactionLimit = planUsage?.plan?.features?.totalTransactions;
  const purchaseUsage = planUsage?.usage?.totalPurchaseOrders;
  const transactionUsage = planUsage?.usage?.totalTransactions;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isPurchaseLimitReached =
    typeof purchaseLimit === "number" &&
    typeof purchaseUsage === "number" &&
    purchaseUsage >= purchaseLimit;
  const isTransactionLimitReached =
    typeof transactionLimit === "number" &&
    typeof transactionUsage === "number" &&
    transactionUsage >= transactionLimit;
  const disableCreatePurchase =
    isPlanExpired || isPurchaseLimitReached || isTransactionLimitReached;

  const getPurchases = useCallback(
    (page: number) => {
      dispatch(
        purchaseActions.getPurchaseListRequest({
          page,
          limit,
        })
      );
    },
    [dispatch, limit]
  );

  useEffect(() => {
    getPurchases(1);
  }, [getPurchases]);

  const handlePageChange = (page: number) => getPurchases(page);

  return (
    <DashboardLayout
      meta={{
        title: "Purchase Orders | Invyntra",
        description: "Track and manage all product purchase orders.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Purchase Orders"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Purchases", path: "/app/purchase/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Purchase Orders"
            description="Manage all purchase orders here"
            actions={
              canCreatePurchase ? (
                <Button
                  onClick={() => navigate("/app/purchase/create")}
                  disabled={disableCreatePurchase}
                  title={
                    isPlanExpired
                      ? "Plan expired. Please upgrade."
                      : isPurchaseLimitReached || isTransactionLimitReached
                      ? "Order limit reached. Please upgrade."
                      : undefined
                  }
                >
                  <Plus size={16} className="mr-1" /> Create
                </Button>
              ) : null
            }
          />

          <TableV2
            columns={[
              {
                key: "purchaseOrderId",
                header: "Order ID",
                accessor: ({ row }) =>
                  row.orderStatus === "draft" ? (
                    <span className="font-medium text-gray-500">
                      Order #{row.purchaseOrderId} (draft)
                    </span>
                  ) : (
                    <Link
                      to={`/app/purchase/${row._id}`}
                      className="font-medium text-indigo-600"
                    >
                      Order #{row.purchaseOrderId}
                    </Link>
                  ),
              },
              {
                key: "supplierId.name",
                header: "Supplier",
                accessor: ({ row }) => row.supplierId?.name || "-",
              },
              {
                key: "grandTotal",
                header: "Grand Total",
                align: "right",
                accessor: ({ row }) => currencyFormatter(row.grandTotal),
              },
              {
                key: "paymentStatus",
                header: "Payment Status",
                accessor: ({ row }) => (
                  <Badge
                    text={row.paymentStatus}
                    color={
                      row.paymentStatus === "paid"
                        ? "green"
                        : row.paymentStatus === "failed"
                        ? "red"
                        : "yellow"
                    }
                  />
                ),
              },
              {
                key: "orderStatus",
                header: "Order Status",
                align: "center",
                accessor: ({ row, key }) =>
                  row[key] ? (
                    <Badge
                      color={row[key] === "completed" ? "green" : "yellow"}
                      text={row[key]}
                    />
                  ) : (
                    "-"
                  ),
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Date",
                align: "center",
                accessor: ({ row }) => (
                  <div>
                    {formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A")}
                  </div>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) =>
                  canEditPurchase &&
                  row.orderStatus === "draft" &&
                  (isTenantAdmin || isRowOwner(row)) ? (
                    <div className="pr-2">
                      <button
                        className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                        onClick={() =>
                          navigate(`/app/purchase/${row._id}/edit`)
                        }
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  ) : null,
              },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PurchaseListPage;
